import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IProfessionalFisherCatchFilterOptions } from '@app/interfaces/professionalFisher';

export interface ProfessionalFisherFilterState {
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedFishtypeId: number | null;
  selectedNetNameId: string | null;
  selectedNetType: string | null;
  filterOptions: IProfessionalFisherCatchFilterOptions;
}

const initialState: ProfessionalFisherFilterState = {
  selectedYear: null,
  selectedMonth: null,
  selectedFishtypeId: null,
  selectedNetNameId: null,
  selectedNetType: null,
  filterOptions: {
    years: [],
    months: [],
    fishtypes: [],
    netNames: [],
    netTypes: [],
  },
};

const professionalFisherFiltersSlice = createSlice({
  name: 'professionalFisherFilters',
  initialState,
  reducers: {
    setSelectedYear(state, action: PayloadAction<number | null>) {
      state.selectedYear = action.payload;
    },
    setSelectedMonth(state, action: PayloadAction<number | null>) {
      state.selectedMonth = action.payload;
    },
    setSelectedFishtypeId(state, action: PayloadAction<number | null>) {
      state.selectedFishtypeId = action.payload;
    },
    setSelectedNetNameId(state, action: PayloadAction<string | null>) {
      state.selectedNetNameId = action.payload;
    },
    setSelectedNetType(state, action: PayloadAction<string | null>) {
      state.selectedNetType = action.payload;
    },
    setFilterOptions(state, action: PayloadAction<IProfessionalFisherCatchFilterOptions>) {
      state.filterOptions = action.payload;
    },
    resetFilters(state) {
      state.selectedYear = initialState.selectedYear;
      state.selectedMonth = initialState.selectedMonth;
      state.selectedFishtypeId = initialState.selectedFishtypeId;
      state.selectedNetNameId = initialState.selectedNetNameId;
      state.selectedNetType = initialState.selectedNetType;
    },
  },
});

export const professionalFisherFiltersActions = professionalFisherFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { professionalFisherFilters: ProfessionalFisherFilterState }) => {
  const { selectedYear, selectedMonth, selectedFishtypeId, selectedNetNameId, selectedNetType } =
    state.professionalFisherFilters;

  return (
    selectedYear !== null ||
    selectedMonth !== null ||
    selectedFishtypeId !== null ||
    selectedNetNameId !== null ||
    selectedNetType !== null
  );
};

export const activeFiltersCountSelector = (state: { professionalFisherFilters: ProfessionalFisherFilterState }) => {
  const { selectedYear, selectedMonth, selectedFishtypeId, selectedNetNameId, selectedNetType } =
    state.professionalFisherFilters;

  let count = 0;
  if (selectedYear !== null) count++;
  if (selectedMonth !== null) count++;
  if (selectedFishtypeId !== null) count++;
  if (selectedNetNameId !== null) count++;
  if (selectedNetType !== null) count++;

  return count;
};

// Memoized so the derived `fishtypeIds`/`netName`/`netType` arrays only get new references when
// the underlying selection actually changes — otherwise `useAppSelector(fetchParamsSelector, shallowEqual)`
// would see a "changed" value on every render (new array literals never `===` the previous ones)
// and re-trigger fetch effects in an infinite loop.
export const fetchParamsSelector = createSelector(
  (state: { professionalFisherFilters: ProfessionalFisherFilterState }) => state.professionalFisherFilters.selectedYear,
  (state: { professionalFisherFilters: ProfessionalFisherFilterState }) =>
    state.professionalFisherFilters.selectedMonth,
  (state: { professionalFisherFilters: ProfessionalFisherFilterState }) =>
    state.professionalFisherFilters.selectedFishtypeId,
  (state: { professionalFisherFilters: ProfessionalFisherFilterState }) =>
    state.professionalFisherFilters.selectedNetNameId,
  (state: { professionalFisherFilters: ProfessionalFisherFilterState }) =>
    state.professionalFisherFilters.selectedNetType,
  (selectedYear, selectedMonth, selectedFishtypeId, selectedNetNameId, selectedNetType) => ({
    year: selectedYear,
    month: selectedMonth,
    fishtypeIds: selectedFishtypeId ? [selectedFishtypeId] : [],
    netName: selectedNetNameId ? [selectedNetNameId] : [],
    netType: selectedNetType ? [selectedNetType] : [],
  }),
);

export const professionalFisherFilters = professionalFisherFiltersSlice.reducer;
